import { html } from '../lib/html.js';
import { onlyDigits } from '../lib/masks.js';
import { busy, confirm } from '../lib/ui.js';
import { UPCOMING_STATUS, fmtDay, fmtKm, maintenances, meterShort, meterUnit, nextText, setMeterUnit } from '../services/maintenances.js';

const hours = () => meterUnit() === 'HOURS';
import { vehicles } from '../services/vehicles.js';
import { pill } from './maintenance-ui.js';
import { Page, go } from './base.js';

/**
 * /vehicles/:vehicleId/km: informar a km atual (dono ou pessoa autorizada). Abre direto pelo pedido mensal
 * (push/e-mail) ou pelo "Atualizar" da tela do veículo: um campo, já com o teclado aberto, e Salvar.
 * A km nunca volta: menor que a atual não salva.
 */
class KmUpdatePage extends Page {
  template() {
    return html`
      <ion-header class="ion-no-border">
        <ion-toolbar>
          <ion-buttons slot="start"><ion-back-button text="Voltar"></ion-back-button></ion-buttons>
          <ion-title>Km atual</ion-title>
        </ion-toolbar>
      </ion-header>
      <ion-content class="ion-padding">
        <div class="narrow body wiz"></div>
      </ion-content>`;
  }

  get vid() {
    if (this.vehicleId) return String(this.vehicleId);
    return decodeURIComponent(location.pathname.match(/\/vehicles\/([^/?#]+)/)?.[1] ?? '');
  }

  setup() {
    this.$('ion-back-button').defaultHref = '/home';
    this.$('.body').addEventListener('click', (e) => {
      const el = e.target.closest('[data-action]');
      if (el?.dataset.action === 'save') this.save(el);
      if (el?.dataset.action === 'vehicle') go(`/vehicles/${this.vid}`, 'back');
    });
  }

  async onEnter() {
    this.$('ion-back-button').defaultHref = `/vehicles/${this.vid}`;
    const body = this.$('.body');
    body.innerHTML = '<div class="center"><ion-spinner></ion-spinner></div>';
    try {
      const [v, m] = await Promise.all([vehicles.get(this.vid), maintenances.ownerList(this.vid)]);
      setMeterUnit(v.vehicle.meterUnit);
      this.label = v.access?.nickname || [v.vehicle.brand, v.vehicle.model].filter(Boolean).join(' ') || v.vehicle.plateFormatted || `Série ${v.vehicle.serialNumber ?? ''}`;
      this.status = m;
      this.renderForm();
    } catch (err) {
      body.innerHTML = html`<div class="center"><ion-icon name="alert-circle-outline" class="empty-icon"></ion-icon><p>${err.status === 404 ? 'Veículo não encontrado.' : err.message}</p><ion-button fill="outline" data-action="vehicle">Voltar</ion-button></div>`.value;
    }
  }

  renderForm() {
    const s = this.status;
    const last = s.currentKm === null ? '' : `${hours() ? 'Último horímetro' : 'Última km'}: ${fmtKm(s.currentKm)}${s.kmDate ? ` em ${fmtDay(s.kmDate)}` : ''}`;
    this.$('.body').innerHTML = html`
      <h1 class="wiz-title">${hours() ? `Quantas horas o ${this.label} marca no horímetro?` : `Quantos km o ${this.label} tem hoje?`}</h1>
      <p class="wiz-lead">${hours() ? 'Olhe no painel da máquina: é o número do horímetro (horas de uso).' : 'Olhe no painel: é o número maior do hodômetro.'}</p>
      <ion-input class="km-input" aria-label="${hours() ? 'Horímetro' : 'Km atual'}" fill="outline" inputmode="numeric" enterkeyhint="done" placeholder="${s.currentKm === null ? '0' : Number(s.currentKm).toLocaleString('pt-BR')}" maxlength="9">
        <span slot="end" class="km-unit">${meterShort()}</span>
      </ion-input>
      <p class="field-error km-error" role="alert"></p>
      ${last ? html`<p class="wiz-hint left">${last}</p>` : ''}
      <div class="wiz-foot"><ion-button expand="block" data-action="save">Salvar</ion-button></div>`.value;
    const input = this.$('.km-input');
    input.addEventListener('ionInput', (e) => {
      const d = onlyDigits(e.target.value).slice(0, 7);
      e.target.value = d ? Number(d).toLocaleString('pt-BR') : '';
      this.$('.km-error').textContent = '';
    });
    input.addEventListener('keyup', (e) => e.key === 'Enter' && this.save(this.$('[data-action="save"]')));
    setTimeout(() => input.setFocus?.(), 350);
  }

  async save(button) {
    const km = Number(onlyDigits(this.$('.km-input').value));
    const err = this.$('.km-error');
    const cur = this.status.currentKm;
    if (!onlyDigits(this.$('.km-input').value)) return (err.textContent = hours() ? 'Informe as horas do horímetro.' : 'Informe a km do painel.');
    if (cur !== null && km < cur) return (err.textContent = `${hours() ? 'O horímetro não pode ser menor que o último registrado' : 'A km não pode ser menor que a última registrada'} (${fmtKm(cur)}).`);
    // zero a mais é o erro mais comum
    if (cur !== null && km - cur > (hours() ? 2000 : 30000) && !(await confirm({ header: `${fmtKm(km)}, confere?`, message: `São ${fmtKm(km - cur)} a mais que ${hours() ? 'o último registro' : 'a última km'}. Confira o número no painel.`, confirmText: 'Está certo', cancelText: 'Corrigir' }))) return;
    await busy(button, async () => {
      try {
        this.renderDone(await maintenances.updateKm(this.vid, km));
      } catch (e) {
        err.textContent = e.message;
      }
    });
  }

  renderDone(s) {
    const byKm = s.upcoming.filter((u) => u.nextDueKm !== null);
    this.$('.body').innerHTML = html`
      <div class="wiz-done"><ion-icon name="checkmark-circle-outline"></ion-icon></div>
      <h1 class="wiz-title">Km atualizada: ${fmtKm(s.currentKm)}</h1>
      <p class="wiz-lead">Obrigado! Assim avisamos cada troca na hora certa.</p>
      ${byKm.length
        ? html`
          <div class="panel">
            ${byKm.map((u) => html`
              <div class="row">
                <span class="dot ${UPCOMING_STATUS[u.status].color}"></span>
                <div class="row-main">
                  <span class="row-title">${u.typeName}</span>
                  <span class="row-sub">${u.kmLeft !== null && u.kmLeft > 0 ? `Faltam ${fmtKm(u.kmLeft)}` : nextText(u)}</span>
                </div>
                ${pill(UPCOMING_STATUS[u.status].color, UPCOMING_STATUS[u.status].label)}
              </div>`)}
          </div>`
        : ''}
      <div class="wiz-foot"><ion-button expand="block" data-action="vehicle">Ver o veículo</ion-button></div>`.value;
  }
}

customElements.define('page-km-update', KmUpdatePage);
